import React, { memo, useCallback, useEffect, useId, useState } from "react";
import { SidebarPanel } from "../sidebar-panel";
import { SidebarOptions } from "../sidebar-options-panel/sidebar-options-list";

import { useSelectedZone } from "../../../hooks/useSelection";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { useSupportedFontFamilies } from "../../../hooks/use-supported-font-families";
import { useLabels } from "../../../hooks/useLabels";

import "./sidebar-font-families-panel.css";
import { PlaceholderImage } from "../../placeholder";
import { useClient } from "../../../hooks/useClient";
import { useSelectedFontFamily } from "../../../hooks/use-selected-font-family";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useUsedFontFamilies } from "../../../hooks/useUsedFontFamilies";
import { labels } from "./labels";

const SidebarFontFamilyItem = memo(function SidebarFontFamilyItem({
	fontFamily,
	isActive,
	client,
	onSelect,
}: {
	fontFamily: ReturnType<typeof useSupportedFontFamilies>[number];
	isActive: boolean;
	client: eXpress.core.IResourceResolver;
	onSelect: ((fontFamilyName: eXpress.FontFamilyName) => void) | undefined;
}) {
	const [hasImageError, setHasImageError] = useState(false);

	return (
		<SidebarOptions.Item
			type="list"
			title={fontFamily.name}
			isActive={isActive}
			onClick={() => onSelect?.(fontFamily.name)}
			className="sidebar-font-families-panel__font"
			style={{ "--font-size": "14px" } as React.CSSProperties}
		>
			{hasImageError ? (
				<span className="sidebar-font-families-panel__fallback">{fontFamily.name}</span>
			) : (
				<PlaceholderImage
					className="sidebar-font-families-panel__font-image"
					loading="lazy"
					src={fontFamily.fullImageUrl.getAbsoluteUrl(client)}
					onError={() => setHasImageError(true)}
				/>
			)}
		</SidebarOptions.Item>
	);
});

export const SidebarFontFamiliesPanel = memo(function SidebarFontFamiliesPanel() {
	const selectedZone = useSelectedZone();
	const dashboard = useTextZoneDashboard();

	const { createErrorHandler } = useErrorHandling();

	const selectedFontFamily = useSelectedFontFamily();
	const fontFamiliesToShow = useSupportedFontFamilies(selectedZone);
	const recommendedFonts = useUsedFontFamilies({
		fromDefinition: true,
		fromConfiguration: true,
		includeDefaultFont: false,
	});
	const [[fontLabel, recommendedFontsLabel, allFontsLabel], areLabelsLoading] = useLabels([
		labels.title,
		labels.recommendedFonts,
		labels.allFonts,
	]);

	const client = useClient();
	const canSetFontFamily = dashboard.canSetFontFamily(selectedZone);

	const textZone = eXpress.core.isAnyTextZone(selectedZone) ? selectedZone : undefined;
	const [initialFontSizeInPt, setInitialFontSizeInPt] = useState(
		textZone?.textSettings.ptFontSize,
	);

	// When a new TextZone is selected, get the initial font size
	// When toggling between fonts, we want to keep the font size the same
	useEffect(() => {
		setInitialFontSizeInPt(textZone?.textSettings.ptFontSize);
	}, [textZone]);

	/** Link SubTitle to Radio Group  */
	const allFontsId = useId();
	const recommendedFontsId = useId();

	const onValueChange = useCallback(
		(selectFontFamilyName: eXpress.FontFamilyName) => {
			if (!canSetFontFamily) return;

			const fontFamily = [...recommendedFonts, ...fontFamiliesToShow].find(
				(ff) => ff.name === selectFontFamilyName,
			);
			if (!fontFamily) return;

			dashboard
				.setFontFamilyAsync(selectedZone, fontFamily, {
					preferedFontSizeInPt: initialFontSizeInPt,
				})
				.catch(createErrorHandler());
		},
		[
			canSetFontFamily,
			dashboard,
			selectedZone,
			recommendedFonts,
			fontFamiliesToShow,
			createErrorHandler,
			initialFontSizeInPt,
		],
	);

	if (areLabelsLoading) return null;

	return (
		<SidebarPanel.Root>
			<SidebarPanel.Header>
				<SidebarPanel.Title>{fontLabel}</SidebarPanel.Title>
			</SidebarPanel.Header>
			<SidebarPanel.Content scrollable fade>
				{recommendedFonts.length > 0 && (
					<>
						<SidebarPanel.SubTitle id={recommendedFontsId}>
							{recommendedFontsLabel}
						</SidebarPanel.SubTitle>
						<SidebarOptions.List type="list">
							{recommendedFonts.map((ff) => {
								const isActive = selectedFontFamily?.name === ff.name;

								return (
									<SidebarFontFamilyItem
										key={ff.key}
										fontFamily={ff}
										isActive={isActive}
										client={client}
										onSelect={canSetFontFamily ? onValueChange : undefined}
									/>
								);
							})}
						</SidebarOptions.List>
					</>
				)}

				<SidebarPanel.SubTitle id={allFontsId}>{allFontsLabel}</SidebarPanel.SubTitle>
				<SidebarOptions.List type="list">
					{fontFamiliesToShow.map((ff) => {
						const isActive = selectedFontFamily?.name === ff.name;

						return (
							<SidebarFontFamilyItem
								key={ff.key}
								fontFamily={ff}
								isActive={isActive}
								client={client}
								onSelect={canSetFontFamily ? onValueChange : undefined}
							/>
						);
					})}
				</SidebarOptions.List>
			</SidebarPanel.Content>
		</SidebarPanel.Root>
	);
});
